import { ReactNode, useCallback } from "react";
import styles from "./table.module.css";
import { Button, LoadingSkeleton, Row, Pagination } from "@narmi/design_system";
import { useEffect, useContext, useState } from "react";

import NotificationContext from "cerulean/src/NotificationContext";
import FilterDialog from "./FilterDialog";
import { CaseQuery } from "./FilterDialog";
import CaseDetailDrawer from "./CaseDetailDrawer";
import SearchBar from "./SearchBar";
import { CASE_RESOLUTIONS_TYPE, CASE_STATUSES_TYPE, CASE_TYPES, CASE_TYPES_TYPE } from "./codesets";

export const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export type Case = {
  deadline: string | undefined;
  created_at: string;
  updated_at: string;
  kind: CASE_TYPES_TYPE;
  status: CASE_STATUSES_TYPE;
  case_identification: string;
  payment_identification: string;
  resolution: CASE_RESOLUTIONS_TYPE;
  resolution_reason: string;
  initiation_reason: string;
  additional_info: string;
  information_request_ids: Array<string> | undefined;
};

export default function CaseTable({ selectedId }: { selectedId: string | undefined }) {
  const { sendNotification } = useContext(NotificationContext);

  const [cases, setCases] = useState([]);
  const [page, setPage] = useState(1);
  const [availablePages, setAvailablePages] = useState([1]);
  const [selectedCase, setSelectedCase]: [Case | null, CallableFunction] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTags, setFilterTags]: [Array<ReactNode>, CallableFunction] = useState([]);
  const [filterQuery, setFilterQuery]: [CaseQuery, CallableFunction] = useState({
    payment_id: undefined,
    start_date: undefined,
    end_date: undefined,
    state: undefined,
    from_amount: undefined,
    to_amount: undefined,
    kind: undefined,
    status: undefined,
    text: undefined,
  });

  useEffect(() => {
    window.scrollTo({ top: 200, left: 0, behavior: "smooth" });
  }, []);

  async function fetchCases(page: number = 1, query: string | null = null) {
    setIsLoading(true);
    const query_str = query === null || query === "" ? "" : `&query=${query}`;
    const res = await fetch(`/v1/fednow/cases?ordering=updated_at&page=${page}${query_str}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      let errorMessage = "Error fetching cases. Please contact support.";
      try {
        const errData = await res.json();
        if (typeof errData.message === "string") {
          errorMessage = errData.message;
        } else {
          errorMessage = String(Object.values(errData)[0]) || errorMessage;
        }
      } finally {
        sendNotification({
          type: "negative",
          text: errorMessage,
        });
        setIsLoading(false);
      }
      return;
    }
    const data = await res.json();
    const results = data.results.sort((a: Case, b: Case) => {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
    setCases(results);
    if (data.next && availablePages.indexOf(page + 1) < 0) {
      setAvailablePages(availablePages.concat([page + 1]));
    }
    setIsLoading(false);
    // an ID was passed in to the URL
    if (selectedId && selectedId.length > 0) {
      const lookupRes = await fetch(`/v1/fednow/cases/${encodeURIComponent(selectedId)}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!lookupRes.ok) {
        return;
      }
      const caseobj = await lookupRes.json();
      setSelectedCase(caseobj);
      setDrawerOpen(true);
    }
  }

  async function refreshCases(page: number = 1, query: CaseQuery | null = null) {
    const copy = { ...query };
    if (copy?.from_amount) {
      copy.from_amount = String(Number(copy.from_amount) * 100);
    }
    if (copy?.to_amount) {
      copy.to_amount = String(Number(copy.to_amount) * 100);
    }
    const queryStr = query ? JSON.stringify(copy) : null;
    await fetchCases(page, queryStr);
  }

  async function refreshSelectedCase() {
    if (!selectedCase) {
      return;
    }
    const lookupRes = await fetch(
      `/v1/fednow/cases/${encodeURIComponent(selectedCase.case_identification)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!lookupRes.ok) {
      return;
    }
    const caseobj = await lookupRes.json();
    setSelectedCase(caseobj);
  }

  useEffect(() => {
    refreshCases();
  }, []);

  return (
    <>
      <SearchBar
        submitCallback={refreshCases}
        setPage={setPage}
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        setFilterDialogOpen={setFilterDialogOpen}
      />
      <FilterDialog
        type="cases"
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
        dialogOpen={filterDialogOpen}
        setDialogOpen={setFilterDialogOpen}
        filterTags={filterTags}
        setFilterTags={(value: Array<ReactNode>) => {
          setFilterTags(value);
          setPage(1);
          refreshCases(1, filterQuery);
        }}
        applyCallback={useCallback(() => {
          setPage(1);
          refreshCases(1, filterQuery);
        }, [filterQuery, page])}
      />
      {cases.length > 0 || isLoading ? (
        <div>
          <CaseTableHeader />
          <div className={styles.fednowTable}>
            <LoadingSkeleton isLoading={isLoading} showTitle={false} lines={5}>
              {cases.map((c: Case) => (
                <CaseRow
                  cs={c}
                  key={c.payment_identification}
                  clickCallback={() => {
                    setSelectedCase(c);
                    setDrawerOpen(true);
                  }}
                />
              ))}
            </LoadingSkeleton>
          </div>
        </div>
      ) : (
        <h4 className="fontFamily--body padding--y--l">
          No cases found.{" "}
          <Button
            kind="plain"
            label="Clear search"
            onClick={() => {
              setFilterQuery({});
              setFilterTags([]);
              refreshCases();
            }}
          />
        </h4>
      )}
      <div className="margin--top">
        <Pagination
          selectedPage={page}
          onPageChange={(p: number) => {
            setPage(p);
            refreshCases(p, filterQuery);
          }}
          totalPages={availablePages.length}
        />
      </div>
      <CaseDetailDrawer
        isOpen={drawerOpen}
        setIsOpen={setDrawerOpen}
        caseobj={selectedCase}
        refresh={refreshSelectedCase}
      />
    </>
  );
}

const CaseTableHeader = () => (
  <div className="padding--bottom--xs padding--top--s padding--x" style={{ width: "100%" }}>
    <Row alignItems="center">
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>CASE ID</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>STATUS</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>TYPE</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>LAST UPDATED</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>DUE</div>
      </Row.Item>
    </Row>
  </div>
);

const CaseRow = ({ cs, clickCallback }: { cs: Case; clickCallback: CallableFunction }) => {
  let typeRow = CASE_TYPES[cs.kind as CASE_TYPES_TYPE].toString();
  if (
    cs.kind !== "information_request" &&
    cs.information_request_ids !== undefined &&
    cs.information_request_ids.length > 0
  ) {
    typeRow = `${typeRow}, +${cs.information_request_ids.length}`;
  }
  return (
    <div
      className={`${styles.fednowTableRow} padding--y padding--x`}
      style={{ width: "100%" }}
      onClick={() => {
        clickCallback();
      }}
    >
      <Row alignItems="center">
        <Row.Item>
          <p className="whiteSpace--truncate">{cs.case_identification}</p>
        </Row.Item>
        <Row.Item>
          <div style={{ textTransform: "capitalize" }}>{cs.status}</div>
        </Row.Item>
        <Row.Item>
          <div>{typeRow}</div>
        </Row.Item>
        <Row.Item>
          <div>{new Date(cs.updated_at).toLocaleString()}</div>
        </Row.Item>
        <Row.Item>
          {cs.deadline ? <div>{new Date(cs.deadline).toLocaleString()}</div> : <div>-</div>}
        </Row.Item>
      </Row>
    </div>
  );
};
